import React from 'react';
import { View, Text, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { useThemeColors } from '@/lib/useThemeColors';
import { useAuth } from '@/lib/supabase';
import { reportError } from '@/lib/errors';

/**
 * Shown by a route-group auth guard when the guard cannot safely admit the user:
 *
 *   - the server role-fetch (useUserRole) errored transiently, or
 *   - the MFA position could not be established (AAL gate 'error' — see
 *     src/lib/auth/aal.ts, where an indeterminate lookup FAILS CLOSED to here
 *     rather than being read as "not enrolled").
 *
 * The user IS authenticated, so we offer a retry rather than bouncing them out.
 *
 * The sign-out escape hatch is REQUIRED, not decoration: the AAL gate can land
 * a user here on a session it will not admit, and without a way out that is a
 * trap with no in-app recovery. Signing out also discards the session that
 * could not be evaluated, which is the right outcome anyway.
 */
export function AuthGuardError({ onRetry }: { onRetry: () => void }) {
  const colors = useThemeColors();
  const router = useRouter();
  const { signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      reportError(err, { context: 'AuthGuardError.signOut', level: 'warning', silent: true });
    }
    router.replace('/login');
  };
  return (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        backgroundColor: colors.surface.primary,
      }}
    >
      <Text
        numberOfLines={2}
        style={{ color: colors.text.primary, fontSize: 18, fontWeight: '600', marginBottom: 8, textAlign: 'center' }}
      >
        Couldn’t load your account
      </Text>
      <Text
        numberOfLines={3}
        style={{ color: colors.text.secondary, fontSize: 14, marginBottom: 20, textAlign: 'center' }}
      >
        Check your connection and try again.
      </Text>
      <View style={{ height: 48, borderRadius: 12, overflow: 'hidden', backgroundColor: colors.primary[600] }}>
        <Pressable
          onPress={onRetry}
          accessibilityRole="button"
          accessibilityLabel="Retry loading your account"
          style={{ flex: 1, paddingHorizontal: 32, alignItems: 'center', justifyContent: 'center' }}
        >
          <Text style={{ color: '#FFFFFF', fontSize: 15, fontWeight: '600' }}>Retry</Text>
        </Pressable>
      </View>
      <Pressable
        onPress={handleSignOut}
        accessibilityRole="button"
        accessibilityLabel="Sign out"
        style={{ marginTop: 16, paddingVertical: 8, paddingHorizontal: 16 }}
      >
        <Text numberOfLines={1} style={{ color: colors.text.tertiary, fontSize: 14 }}>
          Sign out
        </Text>
      </Pressable>
    </View>
  );
}
